import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useSettingsCategory, useUpdateSettingsCategory } from "@/lib/settings";
import { SectionHeader } from "@/components/application/section-headers/section-headers";

/** Čitelné popisky nav položek (fallback = klíč). */
const NAV_LABELS: Record<string, string> = {
  dashboard: "Přehled",
  tokens: "Tokeny",
  paatProfiles: "PAAT profily",
  logs: "Logy",
  transactionLogs: "Transakční logy",
  serverStats: "Statistiky serveru",
  health: "Health",
  apiDocs: "API dokumentace",
  testActivation: "Test aktivace",
  testDeviceReplacement: "Test výměny zařízení",
  testOtpPassword: "Test OTP/heslo",
  testAdmin: "Test admin",
  apiTools: "API nástroje",
  loadtestGenerator: "Loadtest generator",
};

/**
 * UI Settings — přepínače viditelnosti navigačních položek. Uloženo do kategorie
 * `ui` (klíč `navigation`). Po uložení se obnoví nav gating (sidebar).
 */
export function UiSettingsPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useSettingsCategory("ui");
  const update = useUpdateSettingsCategory("ui");
  const [nav, setNav] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const n = (data?.config?.navigation as Record<string, boolean> | undefined) ?? {};
    setNav({ ...n });
  }, [data]);

  const toggle = (k: string) => setNav((cur) => ({ ...cur, [k]: !cur[k] }));

  /** Všechny známé nav klíče (uložené ∪ katalog). */
  const allKeys = () => Array.from(new Set([...Object.keys(nav), ...Object.keys(NAV_LABELS)]));
  const setAll = (visible: boolean) => setNav(() => Object.fromEntries(allKeys().map((k) => [k, visible])));
  const resetDefaults = () => setNav(() => Object.fromEntries(allKeys().map((k) => [k, true]))); // default = vše viditelné

  const save = () => {
    const config = { ...(data?.config ?? {}), navigation: nav };
    update.mutate(config);
  };

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("uiSettings.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("uiSettings.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      {isLoading && <LoadingIndicator />}
      {isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}

      {data && (
        <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" color="secondary" onPress={() => setAll(true)}>{t("uiSettings.showAll")}</Button>
            <Button size="sm" color="secondary" onPress={() => setAll(false)}>{t("uiSettings.hideAll")}</Button>
            <Button size="sm" color="tertiary" onPress={resetDefaults}>{t("uiSettings.resetDefaults")}</Button>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {Object.keys(nav).sort().map((k) => (
              <label key={k} className="flex items-center gap-2 text-sm text-secondary">
                <input type="checkbox" checked={!!nav[k]} onChange={() => toggle(k)} className="size-4" />
                {NAV_LABELS[k] ?? k}
              </label>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" color="primary" isLoading={update.isPending} onPress={save}>
              {t("common.save")}
            </Button>
            {update.isSuccess && <span className="text-sm text-success-primary">{t("uiSettings.saved")}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
